import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Send, X, Loader2, Wand2, RotateCw, Bot, Heart, Pencil, Check, Mic, Square, Volume2, VolumeX } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { startRecording, speak, stopSpeaking, supportsMic } from "@/lib/voice";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/context/ModeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SID_KEY = "gv_director_sid";
const PERSONAS = {
  pixel: {
    name: "Pixel",
    tagline: "Your fun game-making buddy",
    icon: Heart,
    grad: "from-[#f59e0b] to-[#ec4899]",
    greeting: "👋 Hi! I'm Pixel! Tell me what game you want to make and I'll help you, step by step!",
    starters: ["Give me a fun game idea", "Make a game about a dragon", "Help me pick a world"],
  },
  nova: {
    name: "Nova",
    tagline: "Your AI creator co-pilot",
    icon: Bot,
    grad: "from-[#7c3aed] to-[#3b9eff]",
    greeting: "👋 Hi, I'm Nova — your creator co-pilot. Ask me about game design, levels, monetisation or growing your players.",
    starters: ["Give me a game concept", "How do I sell level packs?", "How can I grow my players?"],
  },
};

export default function DirectorAssistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isKid } = useMode();
  const persona = (isKid || user?.role === "kid" || user?.role === "student") ? "pixel" : "nova";
  const P = PERSONAS[persona];
  const PIcon = P.icon;
  // Editor mode: when the user is inside the Game Builder, the Director can APPLY real edits.
  const buildMatch = location.pathname.match(/^\/app\/build\/([^/?#]+)/);
  const editGameId = buildMatch ? buildMatch[1] : null;
  const canEdit = !!editGameId;
  const EDIT_GREETING = "✏️ I'm plugged into this game — tell me what to change and I'll apply it live. E.g. \"make the player faster\", \"add a boss\", \"change the sky to sunset\".";
  const EDIT_STARTERS = ["Make it more challenging", "Add a friendly NPC", "Change the world to a snowy night", "Give the hero a double jump"];
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(null);
  const sidKey = user ? `${SID_KEY}_${user.id}` : SID_KEY;
  const [sid, setSid] = useState(() => localStorage.getItem(sidKey) || "");
  const scroller = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("gv_director_voice") === "1");
  const recRef = useRef(null);
  const pendingClarifyRef = useRef(null);
  const voiceName = persona === "pixel" ? "fable" : "nova";

  const sayIfVoice = (text) => { if (voiceOn && text) speak(text, voiceName); };

  const toggleVoice = () => {
    setVoiceOn((v) => { const nv = !v; localStorage.setItem("gv_director_voice", nv ? "1" : "0"); if (!nv) stopSpeaking(); return nv; });
  };

  const toggleMic = async () => {
    if (transcribing) return;
    if (recording) {
      try { setRecording(false); setTranscribing(true); const text = await recRef.current.stop(); recRef.current = null;
        if (text && text.trim()) send(text.trim());
      } catch (e) { setFailed({ msg: "", micError: true }); }
      finally { setTranscribing(false); }
    } else {
      try { stopSpeaking(); recRef.current = await startRecording(); setRecording(true); }
      catch (e) { setFailed({ msg: "", micError: true }); }
    }
  };

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("gv-open-assistant", openHandler);
    return () => window.removeEventListener("gv-open-assistant", openHandler);
  }, []);

  useEffect(() => {
    if (open && sid && !canEdit) {
      api.get(`/director/history?session_id=${sid}`).then((r) => setMessages(r.data.messages || [])).catch(() => {});
    }
  }, [open, sid, canEdit]);

  // Fresh conversation whenever the builder game changes (or we enter/leave edit mode).
  useEffect(() => {
    setMessages([]); setFailed(null);
  }, [editGameId]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, sending, failed]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput(""); setFailed(null);
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    // When answering a clarifying question, prepend the original request so Studio keeps context.
    const pending = canEdit ? pendingClarifyRef.current : null;
    const instruction = pending ? `${pending} — ${msg}` : msg;
    try {
      if (canEdit) {
        const { data } = await api.post(`/games/${editGameId}/edit`, { instruction }, { timeout: 45000 });
        const changed = !data.answered;
        const clarify = !!data.clarify;
        pendingClarifyRef.current = clarify ? instruction : null;
        setMessages((m) => [...m, { role: "assistant", content: data.summary, applied: changed, options: clarify ? (data.options || []) : [] }]);
        sayIfVoice(data.summary);
        if (data.play_cutscene) {
          window.dispatchEvent(new CustomEvent("gv-director-play-cutscene", { detail: { name: data.play_cutscene } }));
        }
        if (changed) {
          window.dispatchEvent(new CustomEvent("gv-director-edited", {
            detail: { config: data.config, summary: data.summary, version: data.version, model_generating: data.model_generating },
          }));
        }
      } else {
        const { data } = await api.post("/director/chat", { message: msg, session_id: sid || undefined, mode: isKid ? "kid" : "studio" }, { timeout: 30000 });
        if (data.session_id && data.session_id !== sid) { setSid(data.session_id); localStorage.setItem(sidKey, data.session_id); }
        setMessages((m) => [...m, { role: "assistant", content: data.reply, suggestions: data.suggestions || [] }]);
        sayIfVoice(data.reply);
      }
    } catch (e) {
      pendingClarifyRef.current = null;
      const detail = e.response?.data?.detail;
      if (canEdit && detail) {
        setMessages((m) => [...m, { role: "assistant", content: formatApiError(detail) }]);
      } else {
        const timedOut = e.code === "ECONNABORTED" || /timeout/i.test(e.message || "");
        setFailed({ msg, timedOut });
      }
    } finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="director-fab"
        className={`fixed bottom-20 right-5 lg:bottom-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br ${P.grad} text-white grid place-items-center shadow-xl shadow-black/20 hover:scale-105 active:scale-95 transition-transform`}
        aria-label={`Open ${P.name}, your AI assistant`}>
        {open ? <X className="w-6 h-6" /> : <PIcon className="w-6 h-6" />}
      </button>

      {open && (
        <div data-testid="director-panel"
          className="fixed bottom-36 right-5 lg:bottom-24 z-50 w-[min(380px,calc(100vw-2.5rem))] h-[min(540px,calc(100vh-8rem))] rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-2xl flex flex-col overflow-hidden animate-rise">
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[hsl(var(--primary))]/15 to-transparent">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${P.grad} grid place-items-center`}><PIcon className="w-4 h-4 text-white" /></div>
            <div className="min-w-0">
              <div className="font-bold text-sm leading-tight flex items-center gap-1.5" data-testid="assistant-name">
                {P.name}
                {canEdit && (
                  <span data-testid="director-edit-badge" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">
                    <Pencil className="w-2.5 h-2.5" /> Editing
                  </span>
                )}
              </div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{canEdit ? "I'll apply changes to this game" : P.tagline}</div>
            </div>
            <button onClick={toggleVoice} data-testid="director-voice-toggle" title={voiceOn ? "Voice replies on" : "Voice replies off"}
              className={`ml-auto ${voiceOn ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))]"} hover:text-[hsl(var(--foreground))]`}>
              {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button onClick={() => setOpen(false)} data-testid="director-close" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><X className="w-4 h-4" /></button>
          </div>

          <div ref={scroller} className="flex-1 overflow-y-auto p-3.5 space-y-3" data-testid="director-messages">
            {messages.length === 0 && (
              <div className="text-sm text-[hsl(var(--muted-foreground))]">
                <p className="mb-3">{canEdit ? EDIT_GREETING : P.greeting}</p>
                <div className="flex flex-col gap-1.5">
                  {(canEdit ? EDIT_STARTERS : P.starters).map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`director-starter-${i}`}
                      className="text-left text-xs px-3 py-2 rounded-xl border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
                  <div data-testid={`director-msg-${m.role}`}
                    className={`text-sm px-3 py-2 rounded-2xl ${m.role === "user" ? "bg-[hsl(var(--primary))] text-white rounded-br-sm" : "bg-[hsl(var(--muted))] rounded-bl-sm"}`}>
                    {m.content}
                  </div>
                  {m.applied && (
                    <div data-testid={`director-applied-${i}`} className="flex items-center gap-1 mt-1 text-[10px] font-semibold text-emerald-500">
                      <Check className="w-3 h-3" /> Applied to your game
                    </div>
                  )}
                  {m.options?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`director-clarify-options-${i}`}>
                      {m.options.map((opt, j) => (
                        <button key={j} onClick={() => send(opt)} disabled={sending}
                          data-testid={`director-clarify-chip-${i}-${j}`}
                          className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors disabled:opacity-50">
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                  {m.suggestions?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {m.suggestions.map((s, j) => (
                        <button key={j} disabled={!s.route} onClick={() => s.route && (setOpen(false), navigate(s.route))}
                          data-testid={`director-suggestion-${i}-${j}`}
                          className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors disabled:opacity-50">
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {P.name} is thinking…</div>}
            {failed && (
              <div data-testid="director-error" className="rounded-2xl border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 p-3">
                <div className="text-sm text-[hsl(var(--destructive))] font-medium mb-1">
                  {failed.timedOut ? "That took too long to answer." : "I couldn't reach the studio just now."}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2.5">Check your connection and try again.</div>
                <Button size="sm" variant="outline" onClick={() => send(failed.msg)} className="rounded-full h-8" data-testid="director-retry">
                  <RotateCw className="w-3.5 h-3.5 mr-1" /> Retry
                </Button>
              </div>
            )}
            {failed && failed.micError && (
              <div data-testid="director-mic-error" className="rounded-2xl border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 p-3 text-xs text-[hsl(var(--muted-foreground))]">
                I couldn't use the microphone. Please allow mic access and try again.
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[hsl(var(--border))] flex items-center gap-2">
            {supportsMic() && (
              <Button size="icon" variant={recording ? "destructive" : "outline"} onClick={toggleMic} disabled={transcribing}
                className="rounded-full shrink-0" data-testid="director-mic" title={recording ? "Stop & send" : "Speak"}>
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
            )}
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={recording ? "Listening… tap ■ to send" : canEdit ? "Tell me what to change…" : `Ask ${P.name} anything…`} className="rounded-full text-sm" data-testid="director-input" />
            <Button size="icon" onClick={() => send()} disabled={sending || !input.trim()} className="rounded-full shrink-0" data-testid="director-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
